import React from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../config/api';

// Create a shared axios instance for the API so we can centralize baseURL, headers and interceptors
export const api = axios.create({
  baseURL: getApiBaseUrl()
});

// Simple request interceptor for debugging/auth injection if needed
api.interceptors.request.use(
  (config) => {
    // config.headers.Authorization = `Bearer ${token}`; // optionally inject token here
    return config;
  },
  (error) => Promise.reject(error)
);

// Simple response interceptor to unwrap data or log errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Could add centralized error handling here
    return Promise.reject(error);
  }
);

/**
 * Post garment and model images to the AI model endpoint.
 *
 * @param {Object} params
 * @param {string} params.garmentBase64 - base64 image (without data: prefix)
 * @param {string} params.personBase64 - base64 model image (without data: prefix)
 * @param {string} params.garmentCategory - garment category identifier
 * @param {string} params.gender - model gender ('m' for male, 'f' for female)
 * @param {Object} [params.headers={}] - optional request headers (e.g., Authorization)
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function postAiModel({ garmentBase64, personBase64, backgroundBase64, garmentCategory, gender, headers = {} }) {
  return api.post(
    '/ai-model',
    {
      garment_image: garmentBase64,
      model_images: personBase64,
      background_image: backgroundBase64,
      garment_category: garmentCategory,
      gender: gender,
    },
    { headers }
  );
}

/**
 * Post garment and a single human image to the virtual try-on endpoint.
 *
 * @param {Object} params
 * @param {string} params.garmentBase64
 * @param {string} params.personBase64
 * @param {string} params.garmentCategory
 * @param {Object} [params.headers={}] - optional request headers
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function postVirtualTryon({ garmentBase64, personBase64, backgroundBase64, garmentCategory, headers = {} }) {
  return api.post(
    '/virtual-tryon',
    {
      garment_image: garmentBase64,
      human_images: personBase64,
      background_image: backgroundBase64,
      garment_category: garmentCategory,
    },
    { headers }
  );
}

/**
 * Submit a virtual try-on job for final processing (separate endpoint).
 *
 * @param {Object} params
 * @param {string} params.garmentBase64
 * @param {string[]} params.personBase64
 * @param {string} params.garmentCategory
 * @param {string} params.gender - model gender ('m' for male, 'f' for female)
 * @param {Object} [params.headers={}] - optional request headers
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function postSubmitVirtualTryon({ garmentBase64, personBase64, backgroundBase64, garmentCategory, gender, headers = {} }) {
  return api.post(
    '/submit-virtual-tryon',
    {
      garment_image: garmentBase64,
      human_images: personBase64,
      background_image: backgroundBase64,
      garment_category: garmentCategory,
      gender: gender,
    },
    { headers }
  );
}

/**
 * Get status for a virtual-tryon job.
 *
 * @param {string[]} jobIds - job id returned by the server
 * @param {Object} [headers={}] - optional headers (e.g., Authorization)
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function checkVirtualTryonStatus(jobIds, headers = {}) {
  return api.post('/virtual-tryon-status', 
    {
      job_ids: jobIds
    },  
    { headers }
  );
}

/**
 * Get recent activities for the current user.
 *
 * @param {Object} [headers={}] - optional headers (e.g., Authorization)
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function getRecentActivities(headers = {}) {
  return api.get('/recent-activity', { headers });
}

/**
 * Get recent activities for the current user.
 *
 * @param {Object} [headers={}] - optional headers (e.g., Authorization)
 * @returns {Promise<AxiosResponse>} axios response promise
 */

export async function getUserPrivileges(headers = {}) {
  return api.get('/get-user-privileges', { headers });
}

/**
 * Get usage summary for the current user.
 *
 * @param {Object} [headers={}] - optional headers (e.g., Authorization)
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function getUsageSummary(headers = {}) {
  return api.get('/usage-summary', { headers });
}

/**
 * Get billing data for the current user.
 *
 * @param {Object} [headers={}] - optional headers (e.g., Authorization)
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function getBillingData(headers = {}) {
  return api.get('/billing', { headers });
}

/**
 * Get billing portal URL for the current user.
 *
 * @param {Object} [headers={}] - optional headers (e.g., Authorization)
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function getBillingPortalLink(headers = {}) {
  return api.get('/billing/portal', { headers });
}

/**
 * Expand an image by adding pixels in specified direction.
 *
 * @param {Object} params
 * @param {string} params.image - base64 image data
 * @param {string} params.direction - expand direction ('top', 'bottom', 'left', 'right', 'all')
 * @param {number} params.pixels - number of pixels to expand
 * @param {Object} [params.headers={}] - optional request headers
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function postImageExpand({ image, direction, pixels, headers = {} }) {
  return api.post('/expand', {
    image,
    direction,
    pixels,
  }, { headers });
}

/**
 * Upscale an image to higher resolution.
 *
 * @param {Object} params
 * @param {string} params.image - base64 image data
 * @param {Object} [params.headers={}] - optional request headers
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function postImageUpscale({ image, headers = {} }) {
  return api.post('/upscale', {
    image,
  }, { headers });
}

/**
 * Remove background from an image.
 *
 * @param {Object} params
 * @param {string} params.image - base64 image data
 * @param {Object} [params.headers={}] - optional request headers
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function postBackgroundRemove({ image, headers = {} }) {
  return api.post('/background/remove', {
    image,
  }, { headers });
}

/**
 * Swap background of an image with a new background.
 *
 * @param {Object} params
 * @param {string} params.foreground_image - base64 foreground image data
 * @param {string} params.background_image - base64 background image data
 * @param {Object} [params.headers={}] - optional request headers
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function postBackgroundSwap({ foreground_image, background_image, headers = {} }) {
  return api.post('/background/swap', {
    foreground_image,
    background_image,
  }, { headers });
}

/**
 * Send contact form email.
 *
 * @param {Object} params
 * @param {string} params.name - Sender's name
 * @param {string} params.email - Sender's email address
 * @param {string} params.phone - Sender's phone number
 * @param {string} params.message - Message content
 * @param {Object} [params.headers={}] - optional request headers
 * @returns {Promise<AxiosResponse>} axios response promise
 */
export async function sendEmail({ name, email, phone, message, headers = {} }) {
  return api.post(
    '/send-email',
    {
      from_email: "info@gomens.ai",
      from_name: name,
      to_email: 'info@gomens.ai',
      to_name: 'Gomens Support',
      subject: `Contact Form Message from ${name}`,
      html_body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #333; border-bottom: 2px solid #7c3aed; padding-bottom: 10px;">New Contact Form Message</h2>
          <div style="margin: 20px 0;">
            <p><strong>From:</strong> ${name} (${email})</p>
            <p><strong>Phone:</strong> ${phone || 'Not provided'}</p>
          </div>
          <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #333;">Message:</h3>
            <p style="white-space: pre-wrap; margin: 0;">${message}</p>
          </div>
          <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;">
          <p style="color: #666; font-size: 12px;">This message was sent from the Gomens contact form.</p>
        </div>
      `
    },
    { headers }
  );
}

/**
 * ApiClientProvider is a lightweight React wrapper to keep this file consistent
 * with other React modules and allow future context or axios configuration.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.children]
 */
export default function ApiClientProvider({ children = null }) {
  return <>{children}</>;
}
